import { useEffect, useRef } from 'react'

export interface PopoverItem {
  label: string
  icon?: string
  danger?: boolean
  onClick: () => void
}

interface PopoverProps {
  items: PopoverItem[]
  /** 앵커 좌표 (right-bottom 기준) — open 시 결정 */
  anchor: { x: number; y: number } | null
  onClose: () => void
}

export function Popover({ items, anchor, onClose }: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!anchor) return
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose()
    }
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    // mousedown 이 click 보다 먼저 — 같은 click 으로 닫힘 방지를 위해 다음 tick
    setTimeout(() => document.addEventListener('mousedown', onDoc), 0)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onEsc)
    }
  }, [anchor, onClose])

  if (!anchor) return null
  // viewport 안 들어오게 약간 조정
  const left = Math.min(anchor.x, window.innerWidth - 200)
  const top = Math.min(anchor.y, window.innerHeight - items.length * 36 - 16)
  return (
    <div
      id="popover-menu"
      ref={ref}
      role="menu"
      style={{
        position: 'fixed',
        left, top,
        zIndex: 1500,
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-md)',
        boxShadow: 'var(--shadow-popover)',
        padding: 4,
        minWidth: 160,
      }}
    >
      {items.map((it, i) => (
        <button
          key={i}
          type="button"
          role="menuitem"
          onClick={() => { onClose(); it.onClick() }}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            width: '100%', padding: '6px 10px', border: 'none', background: 'transparent',
            textAlign: 'left', cursor: 'pointer', fontSize: '0.875rem',
            color: it.danger ? 'var(--danger)' : 'var(--text)',
            borderRadius: 'var(--r-sm)',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = it.danger ? 'var(--danger-soft)' : 'var(--bg-hover)' }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
        >
          {it.icon && <span style={{ width: 16, height: 16, display: 'inline-flex', alignItems: 'center' }} dangerouslySetInnerHTML={{ __html: it.icon }} />}
          <span>{it.label}</span>
        </button>
      ))}
    </div>
  )
}
